import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowLeft, Users, Sticker, Package, LayoutGrid, Lightbulb, Bell, Plus, Pencil, Trash2, X, Loader2,
  Check, Ban, UserCheck, Star, Upload, CreditCard, Copy, Mail, Palette, Sparkles,
} from "lucide-react";
import { toast } from "sonner";
import { api, apiError, mediaUrl, thumbUrl } from "@/api";
import { loadPng } from "@/lib/pngCache";
import { PackCoverField } from "@/components/PackCoverField";
import { applyStickerBg } from "@/components/Layout";
import { VariationsEditor, variationsFromSticker } from "@/components/VariationsEditor";
import { BulkUploader } from "@/components/BulkUploader";

const TABS = [
  { id: "dashboard", label: "Painel", icon: LayoutGrid },
  { id: "stickers", label: "Stickers", icon: Sticker },
  { id: "categories", label: "Categorias", icon: LayoutGrid },
  { id: "packs", label: "Packs", icon: Package },
  { id: "notices", label: "Avisos", icon: Bell },
  { id: "appearance", label: "Aparência", icon: Palette },
  { id: "suggestions", label: "Sugestões", icon: Lightbulb },
  { id: "users", label: "Usuárias", icon: Users },
  { id: "kiwify", label: "Kiwify", icon: CreditCard },
];

function Modal({ title, children, onClose }) {
  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/30 backdrop-blur-sm p-4" onClick={onClose}>
      <div className="w-full max-w-lg bg-white rounded-3xl p-6 bc-shadow-hover animate-float-up max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-5">
          <h3 className="font-serif text-2xl">{title}</h3>
          <button onClick={onClose} className="text-[#7A7571]" data-testid="admin-modal-close"><X size={20} /></button>
        </div>
        {children}
      </div>
    </div>
  );
}

const inputCls = "w-full h-12 px-4 rounded-2xl bg-[#FCFBF9] border border-[#E8CFD1] outline-none focus:border-[#C58B90] transition-colors text-sm";
const btnPrimary = "w-full h-12 rounded-full bg-[#C58B90] text-white font-semibold flex items-center justify-center gap-2 hover:bg-[#A87277] transition-colors disabled:opacity-70";

function Dashboard() {
  const [stats, setStats] = useState(null);
  const [testing, setTesting] = useState(false);
  useEffect(() => {
    api.get("/admin/stats").then((r) => setStats(r.data));
  }, []);
  const sendTest = async () => {
    setTesting(true);
    try {
      const { data } = await api.post("/admin/push/test");
      toast.success(`Notificação enviada para ${data.subscriptions} inscrição(ões) 💗`);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setTesting(false);
    }
  };
  if (!stats) return <div className="py-16 flex justify-center"><Loader2 className="animate-spin text-[#C58B90]" /></div>;
  const cards = [
    { label: "Usuárias", value: stats.users, icon: Users },
    { label: "Pendentes", value: stats.pending_users, icon: UserCheck },
    { label: "Stickers", value: stats.stickers, icon: Sticker },
    { label: "Packs", value: stats.packs, icon: Package },
    { label: "Categorias", value: stats.categories, icon: LayoutGrid },
    { label: "Sugestões", value: stats.suggestions, icon: Lightbulb },
  ];
  return (
    <div className="space-y-5">
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
        {cards.map((c) => (
          <div key={c.label} data-testid={`stat-${c.label}`} className="bg-white rounded-3xl p-5 border border-[#F2E6E7] bc-shadow-soft">
            <div className="h-11 w-11 rounded-2xl bg-[#F2E6E7] flex items-center justify-center mb-3">
              <c.icon size={20} className="text-[#C58B90]" />
            </div>
            <p className="font-serif text-4xl text-[#2C2A29]">{c.value}</p>
            <p className="text-sm text-[#7A7571]">{c.label}</p>
          </div>
        ))}
      </div>
      <div className="bg-white rounded-3xl p-5 border border-[#F2E6E7] bc-shadow-soft flex items-center justify-between gap-3 flex-wrap">
        <div>
          <p className="font-medium flex items-center gap-2"><Bell size={17} className="text-[#C58B90]" /> Notificações push</p>
          <p className="text-xs text-[#7A7571] mt-0.5">As clientes são avisadas automaticamente a cada novo sticker. Envie um teste para conferir.</p>
        </div>
        <button
          onClick={sendTest}
          disabled={testing}
          data-testid="push-test-btn"
          className="inline-flex items-center gap-2 h-11 px-5 rounded-full bg-[#C58B90] text-white text-sm font-medium hover:bg-[#A87277] transition-colors disabled:opacity-70"
        >
          {testing ? <Loader2 size={16} className="animate-spin" /> : <Bell size={16} />} Enviar teste
        </button>
      </div>
    </div>
  );
}

function ManageCategories() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: "", emoji: "✨", order: 0 });
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/categories").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing("new"); setForm({ name: "", emoji: "✨", order: items.length }); };
  const openEdit = (c) => { setEditing(c.id); setForm({ name: c.name, emoji: c.emoji, order: c.order }); };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editing === "new") await api.post("/categories", form);
      else await api.put(`/categories/${editing}`, form);
      toast.success("Categoria salva 💗");
      setEditing(null);
      load();
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };

  const del = async (id) => {
    if (!window.confirm("Excluir esta categoria?")) return;
    await api.delete(`/categories/${id}`);
    toast.success("Categoria excluída");
    load();
  };

  return (
    <div className="space-y-4">
      <button onClick={openNew} data-testid="add-category" className="inline-flex items-center gap-2 h-11 px-5 rounded-full bg-[#C58B90] text-white text-sm font-medium hover:bg-[#A87277] transition-colors"><Plus size={17} /> Nova categoria</button>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {items.map((c) => (
          <div key={c.id} className="bg-white rounded-2xl p-4 border border-[#F2E6E7] flex items-center gap-3">
            <span className="text-2xl">{c.emoji}</span>
            <div className="flex-1"><p className="font-medium">{c.name}</p><p className="text-xs text-[#7A7571]">{c.sticker_count} stickers</p></div>
            <button onClick={() => openEdit(c)} data-testid={`edit-category-${c.id}`} className="p-2 text-[#C58B90]"><Pencil size={16} /></button>
            <button onClick={() => del(c.id)} data-testid={`delete-category-${c.id}`} className="p-2 text-[#C0555A]"><Trash2 size={16} /></button>
          </div>
        ))}
      </div>
      {editing && (
        <Modal title={editing === "new" ? "Nova categoria" : "Editar categoria"} onClose={() => setEditing(null)}>
          <form onSubmit={save} className="space-y-4">
            <input className={inputCls} placeholder="Nome" data-testid="category-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required />
            <input className={inputCls} placeholder="Emoji" data-testid="category-emoji" value={form.emoji} onChange={(e) => setForm({ ...form, emoji: e.target.value })} />
            <input className={inputCls} type="number" placeholder="Ordem" value={form.order} onChange={(e) => setForm({ ...form, order: parseInt(e.target.value) || 0 })} />
            <button className={btnPrimary} disabled={saving} data-testid="save-category">{saving && <Loader2 size={17} className="animate-spin" />} Salvar</button>
          </form>
        </Modal>
      )}
    </div>
  );
}

function ManagePacks() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: "", emoji: "✨", description: "", featured: false, seasonal: false, season_label: "", start_date: "", end_date: "", cover_path: "" });
  const [saving, setSaving] = useState(false);
  const [packStickers, setPackStickers] = useState([]);

  const load = () => api.get("/packs").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing("new"); setPackStickers([]); setForm({ name: "", emoji: "✨", description: "", featured: false, seasonal: false, season_label: "", start_date: "", end_date: "", cover_path: "" }); };
  const openEdit = (p) => {
    setEditing(p.id);
    setPackStickers([]);
    api.get(`/packs/${p.id}`).then((r) => setPackStickers(r.data.stickers || [])).catch(() => {});
    setForm({ name: p.name, emoji: p.emoji, description: p.description, featured: p.featured, seasonal: p.seasonal || false, season_label: p.season_label || "", start_date: p.start_date || "", end_date: p.end_date || "", cover_path: p.cover_path || "" });
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editing === "new") await api.post("/packs", form);
      else await api.put(`/packs/${editing}`, form);
      toast.success("Pack salvo 💗");
      setEditing(null);
      load();
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };

  const del = async (id) => {
    if (!window.confirm("Excluir este pack?")) return;
    await api.delete(`/packs/${id}`);
    toast.success("Pack excluído");
    load();
  };

  return (
    <div className="space-y-4">
      <button onClick={openNew} data-testid="add-pack" className="inline-flex items-center gap-2 h-11 px-5 rounded-full bg-[#C58B90] text-white text-sm font-medium hover:bg-[#A87277] transition-colors"><Plus size={17} /> Novo pack</button>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {items.map((p) => (
          <div key={p.id} className="bg-white rounded-2xl p-4 border border-[#F2E6E7] flex items-center gap-3">
            <span className="text-2xl">{p.emoji}</span>
            <div className="flex-1">
              <p className="font-medium flex items-center gap-1">{p.name} {p.featured && <Star size={13} className="fill-[#C58B90] text-[#C58B90]" />}</p>
              <p className="text-xs text-[#7A7571]">{p.sticker_count} stickers</p>
            </div>
            <button onClick={() => openEdit(p)} data-testid={`edit-pack-${p.id}`} className="p-2 text-[#C58B90]"><Pencil size={16} /></button>
            <button onClick={() => del(p.id)} data-testid={`delete-pack-${p.id}`} className="p-2 text-[#C0555A]"><Trash2 size={16} /></button>
          </div>
        ))}
      </div>
      {editing && (
        <Modal title={editing === "new" ? "Novo pack" : "Editar pack"} onClose={() => setEditing(null)}>
          <form onSubmit={save} className="space-y-4">
            <input className={inputCls} placeholder="Nome" data-testid="pack-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required />
            <input className={inputCls} placeholder="Emoji" data-testid="pack-emoji" value={form.emoji} onChange={(e) => setForm({ ...form, emoji: e.target.value })} />
            <textarea className={inputCls + " h-24 py-3 resize-none"} placeholder="Descrição" data-testid="pack-description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
            <PackCoverField value={form.cover_path} stickers={packStickers} onChange={(cover_path) => setForm((f) => ({ ...f, cover_path }))} />
            <label className="flex items-center gap-3 cursor-pointer">
              <input type="checkbox" data-testid="pack-featured" checked={form.featured} onChange={(e) => setForm({ ...form, featured: e.target.checked })} className="h-5 w-5 accent-[#C58B90]" />
              <span className="text-sm">Destaque na Home</span>
            </label>
            <label className="flex items-center gap-3 cursor-pointer">
              <input type="checkbox" data-testid="pack-seasonal" checked={form.seasonal} onChange={(e) => setForm({ ...form, seasonal: e.target.checked })} className="h-5 w-5 accent-[#C58B90]" />
              <span className="text-sm">Coleção sazonal (banner rotativo na Home)</span>
            </label>
            {form.seasonal && (
              <input className={inputCls} placeholder="Etiqueta sazonal (ex: Especial Dia das Mães)" data-testid="pack-season-label" value={form.season_label} onChange={(e) => setForm({ ...form, season_label: e.target.value })} />
            )}
            {form.seasonal && (
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs text-[#7A7571] ml-1">Início (opcional)</label>
                  <input className={inputCls} type="date" data-testid="pack-start-date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} />
                </div>
                <div>
                  <label className="text-xs text-[#7A7571] ml-1">Fim (opcional)</label>
                  <input className={inputCls} type="date" data-testid="pack-end-date" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} />
                </div>
              </div>
            )}
            {form.seasonal && (
              <p className="text-xs text-[#7A7571] -mt-1">O banner sazonal aparece na Home só entre as datas. Deixe em branco para mostrar sempre.</p>
            )}
            <button className={btnPrimary} disabled={saving} data-testid="save-pack">{saving && <Loader2 size={17} className="animate-spin" />} Salvar</button>
          </form>
        </Modal>
      )}
    </div>
  );
}

function ManageStickers() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [missingCount, setMissingCount] = useState(0);
  const [adminSearch, setAdminSearch] = useState("");
  const [loadingMore, setLoadingMore] = useState(false);
  const [cats, setCats] = useState([]);
  const [packs, setPacks] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: "", keywords: "", category_id: "", pack_id: "", is_new: false });
  const [vars, setVars] = useState([]);
  const [saving, setSaving] = useState(false);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkBusy, setBulkBusy] = useState(false);
  const [kwBusy, setKwBusy] = useState(false);
  const [fillProgress, setFillProgress] = useState(null);
  const autoKwFile = useRef(null);

  const mergeKw = (current, extra) => {
    const list = [...current.split(","), ...extra].map((k) => k.trim().toLowerCase()).filter(Boolean);
    return [...new Set(list)].join(", ");
  };

  const suggestKw = async () => {
    const first = vars[0];
    if (!first) { toast.error("Adicione o PNG da figurinha primeiro."); return; }
    setKwBusy(true);
    try {
      const blob = first.file || (await loadPng(mediaUrl(first.storage_path))).blob;
      const fd = new FormData();
      fd.append("file", blob, first.file ? first.file.name : "sticker.png");
      fd.append("filename", first.file ? first.file.name : form.name);
      const r = await api.post("/admin/keywords/suggest", fd);
      setForm((f) => ({ ...f, keywords: mergeKw(f.keywords, r.data.keywords) }));
      toast.success("Palavras-chave geradas! Revise antes de salvar 💗");
    } catch (e) { toast.error(apiError(e)); } finally { setKwBusy(false); }
  };

  useEffect(() => {
    const file = vars[0] && vars[0].file;
    if (!editing || !file || autoKwFile.current === file || form.keywords.trim()) return;
    autoKwFile.current = file;
    suggestKw();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vars, editing]);

  const fillMissing = async () => {
    const list = [];
    for (let skip = 0; ; skip += 200) {
      const r = await api.get("/stickers", { params: { missing_keywords: true, skip, limit: 200 } });
      list.push(...r.data);
      if (r.data.length < 200) break;
    }
    let ok = 0;
    setFillProgress({ done: 0, total: list.length });
    for (let i = 0; i < list.length; i++) {
      try { await api.post(`/admin/stickers/${list[i].id}/auto-keywords`); ok += 1; } catch (e) {}
      setFillProgress({ done: i + 1, total: list.length });
    }
    setFillProgress(null);
    await load();
    if (ok === list.length) toast.success(`${ok} figurinha(s) ganharam palavras-chave! 💗`);
    else toast(`${ok} de ${list.length} preenchidas. Toque de novo para tentar as que faltaram.`);
  };
  const PAGE = 60;
  const fetchPage = (skip) => api.get("/stickers", { params: { skip, limit: PAGE, search: adminSearch.trim() || undefined } });
  const load = async () => {
    const [r, m] = await Promise.all([fetchPage(0), api.get("/stickers", { params: { missing_keywords: true, limit: 1 } })]);
    setItems(r.data);
    setTotal(Number(r.headers["x-total-count"] || r.data.length));
    setMissingCount(Number(m.headers["x-total-count"] || 0));
  };
  const loadMore = async () => {
    setLoadingMore(true);
    try {
      const r = await fetchPage(items.length);
      setItems((prev) => [...prev, ...r.data.filter((s) => !prev.some((p) => p.id === s.id))]);
    } finally { setLoadingMore(false); }
  };
  useEffect(() => {
    const t = setTimeout(() => { load(); }, adminSearch ? 350 : 0);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [adminSearch]);
  useEffect(() => {
    api.get("/categories").then((r) => setCats(r.data));
    api.get("/packs").then((r) => setPacks(r.data));
  }, []);

  const openNew = () => { setEditing("new"); setForm({ name: "", keywords: "", category_id: "", pack_id: "", is_new: false }); setVars([]); };
  const openEdit = (s) => {
    setEditing(s.id);
    setForm({ name: s.name, keywords: (s.keywords || []).join(", "), category_id: s.category_id || "", pack_id: s.pack_id || "", is_new: s.is_new });
    setVars(variationsFromSticker(s));
  };

  const save = async (e) => {
    e.preventDefault();
    if (vars.length === 0) { toast.error("Adicione pelo menos 1 PNG da figurinha."); return; }
    setSaving(true);
    const fd = new FormData();
    fd.append("name", form.name);
    fd.append("keywords", form.keywords);
    fd.append("category_id", form.category_id);
    fd.append("pack_id", form.pack_id);
    fd.append("is_new", form.is_new);
    fd.append("variations_meta", JSON.stringify(vars.map((v) => ({ key: v.key, label: v.label, hex: v.hex, storage_path: v.file ? null : v.storage_path }))));
    vars.filter((v) => v.file).forEach((v) => fd.append("files", v.file));
    try {
      if (editing === "new") await api.post("/admin/stickers/variations", fd);
      else await api.put(`/admin/stickers/${editing}/variations`, fd);
      toast.success("Sticker salvo 💗");
      setEditing(null);
      await load();
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };

  const openBulk = () => setBulkOpen(true);

  const del = async (id) => {
    if (!window.confirm("Excluir este sticker?")) return;
    await api.delete(`/stickers/${id}`);
    toast.success("Sticker excluído");
    load();
  };

  const toggleNew = async (s) => {
    const fd = new FormData();
    fd.append("name", s.name);
    fd.append("keywords", (s.keywords || []).join(", "));
    fd.append("category_id", s.category_id || "");
    fd.append("pack_id", s.pack_id || "");
    fd.append("is_new", !s.is_new);
    await api.put(`/stickers/${s.id}`, fd);
    load();
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-2">
        <button onClick={openNew} data-testid="add-sticker" className="inline-flex items-center gap-2 h-11 px-5 rounded-full bg-[#C58B90] text-white text-sm font-medium hover:bg-[#A87277] transition-colors"><Plus size={17} /> Novo sticker</button>
        <button onClick={openBulk} data-testid="add-bulk" className="inline-flex items-center gap-2 h-11 px-5 rounded-full bg-white border border-[#C58B90] text-[#C58B90] text-sm font-medium hover:bg-[#F7EBEC] transition-colors"><Upload size={17} /> Adicionar várias</button>
        <span className="inline-flex items-center h-11 px-4 rounded-full bg-[#F2E6E7] text-[#8A5F63] text-sm font-medium" data-testid="sticker-count">{total} stickers</span>
        {(missingCount > 0 || fillProgress) && (
          <button onClick={fillMissing} disabled={!!fillProgress} data-testid="fill-missing-keywords" className="inline-flex items-center gap-2 h-11 px-5 rounded-full bg-white border border-[#C58B90] text-[#C58B90] text-sm font-medium hover:bg-[#F7EBEC] transition-colors disabled:opacity-70">
            {fillProgress ? <Loader2 size={16} className="animate-spin" /> : <Sparkles size={16} />}
            {fillProgress ? `Gerando ${fillProgress.done}/${fillProgress.total}…` : `Gerar palavras-chave (${missingCount} sem)`}
          </button>
        )}
      </div>
      <input className={inputCls} placeholder="Buscar figurinha por nome ou palavra-chave" data-testid="admin-sticker-search" value={adminSearch} onChange={(e) => setAdminSearch(e.target.value)} />
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
        {items.map((s) => (
          <div key={s.id} className="bg-white rounded-2xl p-3 border border-[#F2E6E7]" style={{ contentVisibility: "auto", containIntrinsicSize: "260px" }}>
            <div className="sticker-canvas rounded-xl aspect-square flex items-center justify-center overflow-hidden">
              <img src={thumbUrl(s.storage_path)} alt={s.name} loading="lazy" decoding="async" className="h-[85%] object-contain" />
            </div>
            <p className="text-sm font-medium mt-2 truncate">{s.name || "\u00A0"}</p>
            <div className="flex items-center gap-1 mt-1 h-3" data-testid={`admin-sticker-colors-${s.id}`}>
              {(s.variations || []).map((v) => <span key={v.key} title={v.label} className="h-3 w-3 rounded-full border border-black/15" style={{ backgroundColor: v.hex }} />)}
            </div>
            <div className="flex items-center gap-1 mt-2">
              <button onClick={() => toggleNew(s)} data-testid={`toggle-new-${s.id}`} className={`flex-1 h-8 rounded-full text-[10px] font-bold uppercase tracking-wider transition-colors ${s.is_new ? "bg-[#C58B90] text-white" : "bg-[#F2E6E7] text-[#C58B90]"}`}>Novo</button>
              <button onClick={() => openEdit(s)} data-testid={`edit-sticker-${s.id}`} className="h-8 w-8 rounded-full bg-[#F2E6E7] text-[#C58B90] flex items-center justify-center"><Pencil size={14} /></button>
              <button onClick={() => del(s.id)} data-testid={`delete-sticker-${s.id}`} className="h-8 w-8 rounded-full bg-[#FBEDEE] text-[#C0555A] flex items-center justify-center"><Trash2 size={14} /></button>
            </div>
          </div>
        ))}
      </div>
      {items.length < total && (
        <button onClick={loadMore} disabled={loadingMore} data-testid="admin-load-more" className="w-full h-12 rounded-full bg-white border border-[#C58B90] text-[#C58B90] text-sm font-semibold hover:bg-[#F7EBEC] transition-colors inline-flex items-center justify-center gap-2 disabled:opacity-70">
          {loadingMore && <Loader2 size={16} className="animate-spin" />} Carregar mais ({items.length} de {total})
        </button>
      )}
      {editing && (
        <Modal title={editing === "new" ? "Novo sticker" : "Editar sticker"} onClose={() => setEditing(null)}>
          <form onSubmit={save} className="space-y-4">
            <VariationsEditor value={vars} onChange={setVars} />
            <input className={inputCls} placeholder="Nome" data-testid="sticker-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
            <div>
              <div className="flex gap-2">
                <input className={inputCls} placeholder="Palavras-chave (separadas por vírgula)" data-testid="sticker-keywords" value={form.keywords} onChange={(e) => setForm({ ...form, keywords: e.target.value })} />
                <button type="button" onClick={suggestKw} disabled={kwBusy} data-testid="sticker-keywords-ai" title="Gerar com IA" className="shrink-0 inline-flex items-center gap-1.5 px-4 rounded-2xl bg-[#F7EBEC] text-[#B07A7F] text-sm font-semibold hover:bg-[#EFD9DB] transition-colors disabled:opacity-70">
                  {kwBusy ? <Loader2 size={16} className="animate-spin" /> : <Sparkles size={16} />} IA
                </button>
              </div>
              <p className="mt-1 text-[11px] text-[#7A7571]" data-testid="sticker-keywords-hint">
                {kwBusy ? "Lendo a frase da figurinha…" : editing === "new" ? "Geradas automaticamente ao enviar o PNG — revise, apague ou acrescente." : "Toque em IA para gerar a partir da imagem — revise antes de salvar."}
              </p>
            </div>
            <select className={inputCls} data-testid="sticker-category" value={form.category_id} onChange={(e) => setForm({ ...form, category_id: e.target.value })}>
              <option value="">Sem categoria</option>
              {cats.map((c) => <option key={c.id} value={c.id}>{c.emoji} {c.name}</option>)}
            </select>
            <select className={inputCls} data-testid="sticker-pack" value={form.pack_id} onChange={(e) => setForm({ ...form, pack_id: e.target.value })}>
              <option value="">Sem pack</option>
              {packs.map((p) => <option key={p.id} value={p.id}>{p.emoji} {p.name}</option>)}
            </select>
            <label className="flex items-center gap-3 cursor-pointer">
              <input type="checkbox" data-testid="sticker-isnew" checked={form.is_new} onChange={(e) => setForm({ ...form, is_new: e.target.checked })} className="h-5 w-5 accent-[#C58B90]" />
              <span className="text-sm">Marcar como NOVO ✨</span>
            </label>
            <button className={btnPrimary} disabled={saving} data-testid="save-sticker">{saving && <Loader2 size={17} className="animate-spin" />} Salvar</button>
          </form>
        </Modal>
      )}

      {bulkOpen && (
        <Modal title="Adicionar várias figurinhas" onClose={() => !bulkBusy && setBulkOpen(false)}>
          <BulkUploader cats={cats} packs={packs} inputCls={inputCls} btnPrimary={btnPrimary} onDone={load} onBusyChange={setBulkBusy} />
        </Modal>
      )}
    </div>
  );
}

function ManageNotices() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ message: "", active: true });
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/notices").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing("new"); setForm({ message: "", active: true }); };
  const openEdit = (n) => { setEditing(n.id); setForm({ message: n.message, active: n.active }); };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editing === "new") await api.post("/notices", form);
      else await api.put(`/notices/${editing}`, form);
      toast.success("Aviso salvo 💗");
      setEditing(null);
      load();
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };

  const del = async (id) => {
    if (!window.confirm("Excluir este aviso?")) return;
    await api.delete(`/notices/${id}`);
    load();
  };

  return (
    <div className="space-y-4">
      <button onClick={openNew} data-testid="add-notice" className="inline-flex items-center gap-2 h-11 px-5 rounded-full bg-[#C58B90] text-white text-sm font-medium hover:bg-[#A87277] transition-colors"><Plus size={17} /> Novo aviso</button>
      <div className="space-y-3">
        {items.map((n) => (
          <div key={n.id} className="bg-white rounded-2xl p-4 border border-[#F2E6E7] flex items-center gap-3">
            <div className="flex-1">
              <p className="text-sm">{n.message}</p>
              <span className={`text-[10px] font-bold uppercase tracking-wider ${n.active ? "text-[#5FA36A]" : "text-[#B8B2AD]"}`}>{n.active ? "Ativo" : "Inativo"}</span>
            </div>
            <button onClick={() => openEdit(n)} data-testid={`edit-notice-${n.id}`} className="p-2 text-[#C58B90]"><Pencil size={16} /></button>
            <button onClick={() => del(n.id)} data-testid={`delete-notice-${n.id}`} className="p-2 text-[#C0555A]"><Trash2 size={16} /></button>
          </div>
        ))}
      </div>
      {editing && (
        <Modal title={editing === "new" ? "Novo aviso" : "Editar aviso"} onClose={() => setEditing(null)}>
          <form onSubmit={save} className="space-y-4">
            <textarea className={inputCls + " h-28 py-3 resize-none"} placeholder="Ex: ✨ Tem novidade! Adicionamos 30 novos stickers 💗" data-testid="notice-message" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} required />
            <label className="flex items-center gap-3 cursor-pointer">
              <input type="checkbox" data-testid="notice-active" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} className="h-5 w-5 accent-[#C58B90]" />
              <span className="text-sm">Aviso ativo (exibe na Home)</span>
            </label>
            <button className={btnPrimary} disabled={saving} data-testid="save-notice">{saving && <Loader2 size={17} className="animate-spin" />} Salvar</button>
          </form>
        </Modal>
      )}
    </div>
  );
}

function ManageAppearance() {
  const [color, setColor] = useState("#F6E3E3");
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    api.get("/settings").then((r) => setColor(r.data.sticker_bg || "#F6E3E3"));
  }, []);

  const presets = [
    { label: "Blush claro", value: "#F6E3E3" },
    { label: "Rosé médio", value: "#B87F84" },
    { label: "Rosé escuro", value: "#A0656B" },
    { label: "Rosa antigo", value: "#C58B90" },
    { label: "Terracota rosado", value: "#C08272" },
    { label: "Malva", value: "#B593A0" },
    { label: "Blush pérola", value: "#F2E6E7" },
    { label: "Branco", value: "#FFFFFF" },
  ];

  const apply = (v) => {
    setColor(v);
    applyStickerBg(v); // live preview
  };

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/admin/settings", { sticker_bg: color });
      applyStickerBg(data.sticker_bg);
      toast.success("Cor dos cards atualizada 💗");
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-5">
      <div className="bg-white rounded-3xl p-5 border border-[#F2E6E7] bc-shadow-soft">
        <h3 className="font-serif text-xl mb-1">Cor de fundo dos cards</h3>
        <p className="text-sm text-[#7A7571] mb-4">
          Fundo da vitrine onde as figurinhas aparecem. O padrão é blush claro; use um tom mais escuro se tiver muitas figurinhas brancas. Vale para todo o site.
        </p>

        <div className="flex flex-wrap gap-2 mb-5">
          {presets.map((p) => (
            <button
              key={p.value}
              onClick={() => apply(p.value)}
              data-testid={`bg-preset-${p.value.replace('#','')}`}
              className={`flex items-center gap-2 h-10 pl-2 pr-4 rounded-full border text-sm font-medium transition-colors ${
                color.toLowerCase() === p.value.toLowerCase() ? "border-[#C58B90] bg-[#F7EBEC]" : "border-[#E8CFD1] bg-white"
              }`}
            >
              <span className="h-6 w-6 rounded-full border border-black/10" style={{ backgroundColor: p.value }} />
              {p.label}
            </button>
          ))}
        </div>

        <div className="flex items-center gap-3 mb-5">
          <label className="text-sm text-[#7A7571]">Cor personalizada:</label>
          <input
            type="color"
            data-testid="bg-color-input"
            value={color}
            onChange={(e) => apply(e.target.value)}
            className="h-10 w-16 rounded-lg border border-[#E8CFD1] cursor-pointer bg-white"
          />
          <span className="text-sm font-mono text-[#2C2A29]">{color}</span>
        </div>

        <p className="text-xs text-[#7A7571] uppercase tracking-wider mb-2">Prévia</p>
        <div className="grid grid-cols-3 sm:grid-cols-4 gap-3 mb-5">
          {["amor", "frases", "boa_noite"].map((_, i) => (
            <div key={i} className="bg-white rounded-2xl p-2 border border-[#F2E6E7]">
              <div className="sticker-canvas rounded-xl aspect-square flex items-center justify-center text-white/90 text-xs font-serif">
                Aa ✨
              </div>
            </div>
          ))}
        </div>

        <button onClick={save} disabled={saving} data-testid="save-appearance" className={btnPrimary + " sm:w-64"}>
          {saving && <Loader2 size={17} className="animate-spin" />} Salvar cor
        </button>
      </div>
    </div>
  );
}

function ManageSuggestions({ onRead }) {
  const [items, setItems] = useState([]);
  const load = () => api.get("/suggestions").then((r) => setItems(r.data));
  useEffect(() => {
    load();
    api.post("/admin/suggestions/mark-read").then(() => onRead && onRead()).catch(() => {});
  }, []);
  const del = async (id) => { await api.delete(`/suggestions/${id}`); load(); };
  return (
    <div className="space-y-3">
      {items.length === 0 && <p className="text-[#7A7571] py-10 text-center">Nenhuma sugestão ainda.</p>}
      {items.map((s) => (
        <div key={s.id} className="bg-white rounded-2xl p-4 border border-[#F2E6E7]" data-testid={`suggestion-${s.id}`}>
          <div className="flex items-start justify-between gap-3">
            <div>
              <p className="text-sm">{s.text}</p>
              <p className="text-xs text-[#7A7571] mt-1">— {s.user_name || s.user_email}</p>
            </div>
            <button onClick={() => del(s.id)} data-testid={`delete-suggestion-${s.id}`} className="p-2 text-[#C0555A] shrink-0"><Trash2 size={16} /></button>
          </div>
        </div>
      ))}
    </div>
  );
}

function ManageUsers() {
  const [items, setItems] = useState([]);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "client", status: "approved" });
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/admin/users").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const action = async (id, act) => {
    await api.patch(`/admin/users/${id}`, { action: act });
    toast.success("Feito 💗");
    load();
  };
  const del = async (id) => {
    if (!window.confirm("Excluir esta usuária?")) return;
    try { await api.delete(`/admin/users/${id}`); toast.success("Excluída"); load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const create = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/admin/users", form);
      toast.success("Usuária criada 💗");
      setCreating(false);
      setForm({ name: "", email: "", password: "", role: "client", status: "approved" });
      load();
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };

  const statusBadge = (s) => {
    const map = { approved: ["Liberada", "text-[#5FA36A] bg-[#EAF3EB]"], pending: ["Pendente", "text-[#C58B90] bg-[#F2E6E7]"], blocked: ["Bloqueada", "text-[#C0555A] bg-[#FBEDEE]"] };
    const [label, cls] = map[s] || ["—", ""];
    return <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full ${cls}`}>{label}</span>;
  };

  return (
    <div className="space-y-4">
      <button onClick={() => setCreating(true)} data-testid="add-user" className="inline-flex items-center gap-2 h-11 px-5 rounded-full bg-[#C58B90] text-white text-sm font-medium hover:bg-[#A87277] transition-colors"><Plus size={17} /> Criar usuária</button>
      <div className="space-y-3">
        {items.map((u) => (
          <div key={u.id} className="bg-white rounded-2xl p-4 border border-[#F2E6E7]" data-testid={`user-row-${u.id}`}>
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div>
                <p className="font-medium flex items-center gap-2">{u.name} {u.role === "admin" && <span className="text-[10px] font-bold uppercase text-[#C58B90]">Admin</span>}</p>
                <p className="text-xs text-[#7A7571]">{u.email}</p>
              </div>
              {statusBadge(u.status)}
            </div>
            {u.role !== "admin" && (
              <div className="flex items-center gap-2 mt-3 flex-wrap">
                {u.status === "pending" && (
                  <button onClick={() => action(u.id, "approve")} data-testid={`approve-user-${u.id}`} className="inline-flex items-center gap-1 h-9 px-4 rounded-full bg-[#EAF3EB] text-[#5FA36A] text-xs font-semibold"><Check size={14} /> Liberar</button>
                )}
                {u.status !== "blocked" ? (
                  <button onClick={() => action(u.id, "block")} data-testid={`block-user-${u.id}`} className="inline-flex items-center gap-1 h-9 px-4 rounded-full bg-[#FBEDEE] text-[#C0555A] text-xs font-semibold"><Ban size={14} /> Bloquear</button>
                ) : (
                  <button onClick={() => action(u.id, "unblock")} data-testid={`unblock-user-${u.id}`} className="inline-flex items-center gap-1 h-9 px-4 rounded-full bg-[#EAF3EB] text-[#5FA36A] text-xs font-semibold"><UserCheck size={14} /> Desbloquear</button>
                )}
                <button onClick={() => del(u.id)} data-testid={`delete-user-${u.id}`} className="inline-flex items-center gap-1 h-9 px-4 rounded-full bg-[#F2E6E7] text-[#C0555A] text-xs font-semibold"><Trash2 size={14} /> Excluir</button>
              </div>
            )}
          </div>
        ))}
      </div>
      {creating && (
        <Modal title="Criar usuária" onClose={() => setCreating(false)}>
          <form onSubmit={create} className="space-y-4">
            <input className={inputCls} placeholder="Nome" data-testid="new-user-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required />
            <input className={inputCls} type="email" placeholder="Email" data-testid="new-user-email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required />
            <input className={inputCls} type="password" placeholder="Senha" data-testid="new-user-password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} required />
            <select className={inputCls} value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}>
              <option value="approved">Liberada</option>
              <option value="pending">Pendente</option>
            </select>
            <button className={btnPrimary} disabled={saving} data-testid="save-user">{saving && <Loader2 size={17} className="animate-spin" />} Criar</button>
          </form>
        </Modal>
      )}
    </div>
  );
}

function ManageKiwify() {
  const [data, setData] = useState(null);
  const [simEmail, setSimEmail] = useState("");
  const [simName, setSimName] = useState("");
  const [simBusy, setSimBusy] = useState(false);
  const [testTo, setTestTo] = useState("");
  const [testBusy, setTestBusy] = useState(false);
  const testEmail = async (e) => {
    e.preventDefault();
    setTestBusy(true);
    try {
      await api.post("/admin/email/test", { email: testTo });
      toast.success(`Email de teste enviado para ${testTo} 💗 Confira a caixa de entrada e o spam.`);
    } catch (err) { toast.error(apiError(err), { duration: 9000 }); } finally { setTestBusy(false); }
  };
  const load = () => api.get("/admin/kiwify").then((r) => setData(r.data));
  useEffect(() => { load(); }, []);
  if (!data) return <div className="py-16 flex justify-center"><Loader2 className="animate-spin text-[#C58B90]" /></div>;

  const copy = (txt) => { navigator.clipboard?.writeText(txt); toast.success("Copiado 💗"); };
  const resendAccess = async (email) => {
    try {
      await api.post("/admin/kiwify/resend-access", { email });
      toast.success(`Email de acesso enviado para ${email} 💗`);
    } catch (err) { toast.error(apiError(err)); }
  };
  const resultLabel = { created_and_approved: "Conta liberada (novo)", approved_existing: "Conta liberada", blocked: "Bloqueada (reembolso)", ignored: "Ignorado" };

  const simulate = async (e) => {
    e.preventDefault();
    setSimBusy(true);
    try {
      await api.post("/admin/kiwify/simulate", { email: simEmail, name: simName || "Cliente" });
      toast.success(`Compra simulada para ${simEmail} 💗`);
      setSimEmail(""); setSimName("");
      load();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setSimBusy(false);
    }
  };

  return (
    <div className="space-y-5">
      <div className="bg-white rounded-3xl p-5 border border-[#F2E6E7] bc-shadow-soft">
        <h3 className="font-serif text-xl mb-1">Integração Kiwify — acesso fácil</h3>
        <p className="text-sm text-[#7A7571] mb-4">
          Depois de configurado, quem comprar na Kiwify recebe o link do site, entra com o <b>mesmo email da compra</b> e cria a senha na hora. Simples assim 💗
        </p>

        <p className="text-xs font-bold text-[#2C2A29] uppercase tracking-wider mb-1">1. Webhook (cole na Kiwify em Apps → Webhooks)</p>
        <div className="flex items-center gap-2 bg-[#FCFBF9] border border-[#E8CFD1] rounded-2xl p-3 mb-3">
          <code data-testid="kiwify-webhook-url" className="text-xs text-[#2C2A29] break-all flex-1">{data.webhook_url}</code>
          <button onClick={() => copy(data.webhook_url)} data-testid="copy-webhook-url" className="h-9 w-9 rounded-full bg-[#F2E6E7] text-[#C58B90] flex items-center justify-center shrink-0"><Copy size={15} /></button>
        </div>

        <p className="text-xs font-bold text-[#2C2A29] uppercase tracking-wider mb-1">2. Link para o comprador (coloque na entrega/pós-compra da Kiwify)</p>
        <div className="flex items-center gap-2 bg-[#FCFBF9] border border-[#E8CFD1] rounded-2xl p-3">
          <code data-testid="kiwify-first-access-url" className="text-xs text-[#2C2A29] break-all flex-1">{data.first_access_url}</code>
          <button onClick={() => copy(data.first_access_url)} data-testid="copy-first-access-url" className="h-9 w-9 rounded-full bg-[#F2E6E7] text-[#C58B90] flex items-center justify-center shrink-0"><Copy size={15} /></button>
        </div>

        <div className="flex flex-wrap gap-3 mt-4 text-xs">
          <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full font-semibold ${data.configured ? "bg-[#EAF3EB] text-[#5FA36A]" : "bg-[#FFF6EC] text-[#B8860B]"}`}>
            <CreditCard size={13} /> {data.configured ? "Webhook verificado (token ativo)" : "Webhook desligado: falta o token da Kiwify (KIWIFY_WEBHOOK_TOKEN)"}
          </span>
          <span data-testid="kiwify-email-status" className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full font-semibold ${data.email_configured && !data.email_test_mode ? "bg-[#EAF3EB] text-[#5FA36A]" : "bg-[#FFF6EC] text-[#B8860B]"}`}>
            <Mail size={13} /> {!data.email_configured ? "Emails desligados: falta a chave do Resend" : data.email_test_mode ? "Emails em modo de teste: só chegam no email da sua conta Resend" : "Emails ativos (Resend)"}
          </span>
        </div>
      </div>

      <div className="bg-white rounded-3xl p-5 border border-[#F2E6E7] bc-shadow-soft">
        <h4 className="font-serif text-lg mb-1">Testar email de acesso</h4>
        <p className="text-sm text-[#7A7571] mb-3">Envia o mesmo email que a compradora recebe. No modo de teste, use o email da sua conta Resend.</p>
        <form onSubmit={testEmail} className="flex flex-col sm:flex-row gap-3">
          <input className={inputCls + " flex-1"} type="email" placeholder="seu-email@exemplo.com" data-testid="test-email-input" value={testTo} onChange={(e) => setTestTo(e.target.value)} required />
          <button className="h-12 px-6 rounded-full bg-[#C58B90] text-white font-semibold flex items-center justify-center gap-2 hover:bg-[#A87277] transition-colors disabled:opacity-70 shrink-0" disabled={testBusy} data-testid="test-email-submit">
            {testBusy ? <Loader2 size={16} className="animate-spin" /> : <Mail size={16} />} Enviar teste
          </button>
        </form>
      </div>

      <div className="bg-white rounded-3xl p-5 border border-[#F2E6E7] bc-shadow-soft">
        <h4 className="font-serif text-lg mb-1">Testar compra (simulação)</h4>
        <p className="text-sm text-[#7A7571] mb-3">Libere um email como se ele tivesse comprado, para você testar o primeiro acesso.</p>
        <form onSubmit={simulate} className="flex flex-col sm:flex-row gap-3">
          <input className={inputCls + " flex-1"} type="email" placeholder="email@da-compra.com" data-testid="sim-email" value={simEmail} onChange={(e) => setSimEmail(e.target.value)} required />
          <input className={inputCls + " sm:w-40"} placeholder="Nome (opcional)" data-testid="sim-name" value={simName} onChange={(e) => setSimName(e.target.value)} />
          <button className="h-12 px-6 rounded-full bg-[#C58B90] text-white font-semibold flex items-center justify-center gap-2 hover:bg-[#A87277] transition-colors disabled:opacity-70 shrink-0" disabled={simBusy} data-testid="sim-submit">
            {simBusy ? <Loader2 size={16} className="animate-spin" /> : <CreditCard size={16} />} Liberar
          </button>
        </form>
      </div>

      <div>
        <h4 className="font-serif text-lg mb-3">Últimos eventos</h4>
        {data.events.length === 0 ? (
          <p className="text-[#7A7571] py-8 text-center">Nenhum evento ainda. Configure o webhook ou use a simulação acima.</p>
        ) : (
          <div className="space-y-2">
            {data.events.map((e) => (
              <div key={e.id} className="bg-white rounded-2xl p-4 border border-[#F2E6E7] flex items-center justify-between gap-3" data-testid={`kiwify-event-${e.id}`}>
                <div>
                  <p className="text-sm font-medium">{e.email || "—"}</p>
                  <p className="text-xs text-[#7A7571]">{e.source === "simulacao" ? "simulação" : "Kiwify"} · status: {e.status || "—"}</p>
                  {e.email_status && (
                    <p className="text-xs text-[#7A7571]" data-testid={`kiwify-event-email-${e.id}`}>
                      email: {{ sent: "enviado ✓", already_sent: "já enviado antes para este pedido", failed: "falhou ✗", not_configured: "não enviado (email não configurado)" }[e.email_status] || e.email_status}
                    </p>
                  )}
                </div>
                <div className="flex flex-col items-end gap-1.5">
                  <span className="text-xs font-semibold text-[#C58B90] bg-[#F2E6E7] px-3 py-1 rounded-full">{resultLabel[e.result] || e.result}</span>
                  {e.email && ["created_and_approved", "approved_existing"].includes(e.result) && (
                    <button onClick={() => resendAccess(e.email)} data-testid={`kiwify-resend-${e.id}`} className="inline-flex items-center gap-1 text-xs font-medium text-[#B07A7F] hover:text-[#8A5F63] transition-colors">
                      <Mail size={13} /> Reenviar email de acesso
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export function Admin() {
  const navigate = useNavigate();
  const [tab, setTab] = useState("dashboard");
  const [unread, setUnread] = useState(0);

  const loadUnread = () => api.get("/admin/suggestions/unread").then((r) => setUnread(r.data.count)).catch(() => {});
  useEffect(() => { loadUnread(); }, []);

  return (
    <div className="space-y-6">
      <button onClick={() => navigate("/")} className="flex items-center gap-1 text-sm text-[#C58B90] font-medium"><ArrowLeft size={17} /> Voltar ao app</button>
      <h1 className="font-serif text-3xl">Painel Administrativo</h1>
      <div className="flex gap-2 overflow-x-auto scrollbar-hide -mx-1 px-1">
        {TABS.map((t) => (
          <button
            key={t.id}
            data-testid={`admin-tab-${t.id}`}
            onClick={() => setTab(t.id)}
            className={`relative shrink-0 inline-flex items-center gap-1.5 h-10 px-4 rounded-full text-sm font-medium transition-colors ${tab === t.id ? "bg-[#C58B90] text-white" : "bg-white border border-[#E8CFD1] text-[#2C2A29]"}`}
          >
            <t.icon size={15} /> {t.label}
            {t.id === "suggestions" && unread > 0 && (
              <span
                data-testid="suggestions-badge"
                className="absolute -top-1.5 -right-1.5 min-w-[20px] h-5 px-1.5 rounded-full bg-[#E0455A] text-white text-[10px] font-bold flex items-center justify-center border-2 border-[#FCFBF9]"
              >
                {unread}
              </span>
            )}
          </button>
        ))}
      </div>
      <div className="pt-2">
        {tab === "dashboard" && <Dashboard />}
        {tab === "stickers" && <ManageStickers />}
        {tab === "categories" && <ManageCategories />}
        {tab === "packs" && <ManagePacks />}
        {tab === "notices" && <ManageNotices />}
        {tab === "appearance" && <ManageAppearance />}
        {tab === "suggestions" && <ManageSuggestions onRead={() => setUnread(0)} />}
        {tab === "users" && <ManageUsers />}
        {tab === "kiwify" && <ManageKiwify />}
      </div>
    </div>
  );
}
